/**
 * WeeklyReviewWidget - Weekly review, goals, and task summary
 */

import React, { useState, useMemo } from 'react';
import { Target, CheckSquare, MessageSquare, Calendar, Clock } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import ReviewModal from '../ReviewModal';
import type { WeeklyReviewWidgetConfig } from '../../types/widgets';
import { useWeeks } from '../../hooks/useWeeks';
import { useTodos } from '../../hooks/useTodos';
import type { Todo, WeekUpdate } from '../../types';
import { getInboxTasks } from '../../utils/inboxFilter';

interface WeeklyReviewWidgetProps {
  config: WeeklyReviewWidgetConfig;
  widgetId: string;
  windowId: string;
}

export function WeeklyReviewWidget({ config, widgetId, windowId }: WeeklyReviewWidgetProps) {
  const { currentWeek, updateWeek, isLoading } = useWeeks(new Date());
  const { todos } = useTodos();
  const [expanded, setExpanded] = useState(false);
  const inboxCount = useMemo(() => getInboxTasks(todos).length, [todos]);

  // Compute task stats for the week
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const todayTasks = todos.filter(todo => {
    const date = todo.deadline;
    if (!date) return false;
    return date.startsWith(todayStr);
  });

  const weekTasks = currentWeek ? todos.filter(todo => {
    const weekEnd = currentWeek.end_date.split('T')[0];
    const date = todo.deadline;
    if (!date) return false;
    return date.split('T')[0] <= weekEnd;
  }) : [];

  const completedToday = todayTasks.filter(t => t.status === 'completed').length;
  const completedThisWeek = weekTasks.filter(t => t.status === 'completed').length;

  const hasGoals = currentWeek?.goals && currentWeek.goals.length > 0;
  const hasPriorities = currentWeek?.top_priorities && currentWeek.top_priorities.length > 0;
  const hasReflections = currentWeek?.reflections && Array.isArray(currentWeek.reflections) && currentWeek.reflections.some(e => e.response?.trim());

  const handleStartReview = () => {
    setExpanded(true);
  };

  const handleTodoClick = (todo: Todo) => {
    window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
  };

  return (
    <>
    <WidgetWrapper title="Weekly Review" widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
      <div className="p-3 h-full overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-20 text-gray-400">
            Loading...
          </div>
        ) : (
          <div className="space-y-3">
            {/* Week stats bar */}
            {currentWeek && (
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  <Calendar className="w-3 h-3" />
                  <span>
                    {new Date(currentWeek.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' - '}
                    {new Date(currentWeek.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                {currentWeek.is_review_completed ? (
                  <span className="text-green-600 dark:text-green-400 font-medium">Review done</span>
                ) : (
                  <button
                    onClick={handleStartReview}
                    className="text-blue-500 hover:text-blue-700 font-medium"
                  >
                    Start Review
                  </button>
                )}
              </div>
            )}

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
                <div className="text-lg font-bold text-blue-700 dark:text-blue-300">{completedToday}/{todayTasks.length}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400">Today</div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                <div className="text-lg font-bold text-green-700 dark:text-green-300">{completedThisWeek}/{weekTasks.length}</div>
                <div className="text-xs text-green-600 dark:text-green-400">This Week</div>
              </div>
            </div>

            {/* Goals */}
            {config.showGoals !== false && hasGoals && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                  <Target className="w-3.5 h-3.5" />
                  Weekly Goals
                </h4>
                <ul className="space-y-1">
                  {currentWeek!.goals.map((goal, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <CheckSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Top Priorities */}
            {hasPriorities && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                  <Target className="w-3.5 h-3.5" />
                  Top Priorities
                </h4>
                <ol className="list-decimal list-inside space-y-1">
                  {currentWeek!.top_priorities.map((priority, i) => (
                    <li key={i} className="text-sm text-gray-700 dark:text-gray-300">
                      {priority}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Reflections */}
            {config.showReflections !== false && hasReflections && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Reflections
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {currentWeek!.reflections.filter(e => e.response?.trim()).map((entry, i) => (
                    <p key={i} className="mb-1">
                      <span className="font-medium">{entry.prompt}:</span> {entry.response}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Today's upcoming tasks when no review data */}
            {!hasGoals && !hasPriorities && todayTasks.length > 0 && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-medium text-gray-500 mb-2">
                  <Clock className="w-3.5 h-3.5" />
                  Today's Tasks
                </h4>
                <ul className="space-y-1">
                  {todayTasks.slice(0, 5).map(todo => (
                    <li
                      key={todo.id}
                      onClick={() => handleTodoClick(todo)}
                      className="flex items-center gap-2 text-sm p-1 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        todo.status === 'completed' ? 'bg-green-500'
                          : todo.status === 'in_progress' ? 'bg-blue-500'
                          : 'bg-gray-400'
                      }`} />
                      <span className={`truncate ${todo.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                        {todo.title}
                      </span>
                    </li>
                  ))}
                  {todayTasks.length > 5 && (
                    <li className="text-xs text-gray-400 pl-4">+{todayTasks.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}

            {/* Empty state - only when truly nothing */}
            {!hasGoals && !hasPriorities && !hasReflections && todayTasks.length === 0 && (
              <div className="flex flex-col items-center justify-center py-4 text-gray-400">
                <Target className="w-8 h-8 mb-2" />
                <p className="text-sm">No goals set yet</p>
              </div>
            )}

            {/* Review CTA */}
            {currentWeek && !currentWeek.is_review_completed && (
              <button
                onClick={handleStartReview}
                className="w-full py-2 px-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-sm font-medium hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
              >
                Start Weekly Review
              </button>
            )}
            {currentWeek?.is_review_completed && (
              <button
                onClick={handleStartReview}
                className="w-full py-2 px-3 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded text-xs hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
              >
                View Week Details
              </button>
            )}
          </div>
        )}
      </div>
    </WidgetWrapper>

    {expanded && currentWeek && (
      <ReviewModal
        isOpen
        onClose={() => setExpanded(false)}
        reviewType="weekly"
        periodLabel={`Week ${currentWeek.week_number}, ${currentWeek.year}`}
        reflections={currentWeek.reflections}
        goals={currentWeek.goals}
        inboxCount={inboxCount}
        onSave={(data) => updateWeek(currentWeek.id, { ...data, is_review_completed: false } as WeekUpdate)}
        onComplete={(data) => updateWeek(currentWeek.id, { ...data, is_review_completed: true } as WeekUpdate)}
      />
    )}
    </>
  );
}
